import { formatCardNumberInput } from '../../shared/payment-method-form.js';
import { ensureSelect2, focusOpenSelect2Search, select2MediumOptions } from '../../shared/select2.js';
import { initVirtualKeyboard } from '../../shared/virtual-keyboard-core.js';
import { opCfg, opIsMemberSession, opHasPrefilledMember } from './config.js';
import { opModal, openPaymentIsOnline } from './dom.js';
import { syncOpenPaymentSwipe } from './swipe.js';
import {
    savedPaymentsState,
    fetchOpenPaymentSavedPayments,
    setOpenPaymentSavedCards,
    clearOpenPaymentSavedCards,
} from './saved-cards.js';
import {
    hideOpenPaymentAjaxMsg,
    showOpenPaymentAjaxMsg,
    openPaymentSetCardChoicePillDisabled,
    openPaymentSyncCardChoicePillActive,
    openPaymentSyncMethodPills,
    openPaymentSyncPaymentUiState,
    openPaymentClearCcSection,
    openPaymentPrepareOnlineCcShell,
    openPaymentEnableManualCc,
    openPaymentSetManualCcConstraints,
    setOpenPaymentHasSavedCards,
    activateOpenPaymentNewCardUi,
    syncMemberOpenPaymentSavedCardsOnShow,
    syncOpenPaymentForceSurchargeBanner,
    toggleOpenPaymentLayout,
    closeOpenPaymentModal,
} from './ui-state.js';

function ensureOpenPaymentMemberSelect2() {
    const $openPayUserSel = opModal().find('#open_payment_user_id');
    if (
        !$openPayUserSel.length
        || !$openPayUserSel.is('select')
        || opHasPrefilledMember()
        || opIsMemberSession()
    ) {
        return;
    }
    ensureSelect2($openPayUserSel, select2MediumOptions({
        width: '100%',
        allowClear: false,
        minimumResultsForSearch: 0,
        dropdownParent: opModal(),
        placeholder: 'Select member',
        templateResult: function (data) {
            if (!data.element) {
                return data.text;
            }
            const $w = $('<span></span>');
            $w.addClass($(data.element)[0].className);
            $w.text(data.text);
            return $w;
        },
    }));
}

export function bindOpenPaymentAddModalEvents() {
    $(document).off('change.openPayAddMethod', '#addOpenPaymentModal input[name="open_payment_method"]');
    $(document).on('change.openPayAddMethod', '#addOpenPaymentModal input[name="open_payment_method"]', function () {
        if (opIsMemberSession()) {
            return;
        }
        openPaymentSyncMethodPills();
        toggleOpenPaymentLayout();
        syncOpenPaymentSwipe();
    });

    $(document).off('input.openPayAddSwipeAmount', '#addOpenPaymentModal .open-payment-amount-input');
    $(document).on('input.openPayAddSwipeAmount', '#addOpenPaymentModal .open-payment-amount-input', syncOpenPaymentSwipe);

    $(document).off('click.openPayAddMethodPill', '#addOpenPaymentModal [data-open-payment-method-pill]');
    $(document).on('click.openPayAddMethodPill', '#addOpenPaymentModal [data-open-payment-method-pill]', function () {
        if (opIsMemberSession()) {
            return;
        }
        const val = String($(this).data('open-payment-method-pill') || '');
        opModal().find('input[name="open_payment_method"][value="' + val + '"]').prop('checked', true).trigger('change');
    });

    $(document).off('shown.bs.modal.openPayAdd', '#addOpenPaymentModal');
    $(document).on('shown.bs.modal.openPayAdd', '#addOpenPaymentModal', function () {
        if (opHasPrefilledMember()) {
            syncMemberOpenPaymentSavedCardsOnShow();
        }
        ensureOpenPaymentMemberSelect2();
        openPaymentSyncPaymentUiState();
        syncOpenPaymentForceSurchargeBanner();
        syncOpenPaymentSwipe();
        if (opCfg().kioskValidated) {
            initVirtualKeyboard();
        }
    });

    $(document).off('show.bs.modal.openPayAdd', '#addOpenPaymentModal');
    $(document).on('show.bs.modal.openPayAdd', '#addOpenPaymentModal', function () {
        if (openPaymentIsOnline()) {
            openPaymentPrepareOnlineCcShell();
        }
        openPaymentSyncPaymentUiState();
        syncOpenPaymentForceSurchargeBanner();
    });

    $(document).off('change.openPayAddUser', '#open_payment_user_id');
    $(document).on('change.openPayAddUser', '#open_payment_user_id', function () {
        if (opHasPrefilledMember() || opIsMemberSession()) {
            return;
        }
        const isOnline = openPaymentIsOnline();
        if (!isOnline) {
            openPaymentSyncPaymentUiState();
            return;
        }

        const uid = $(this).val();
        const p = '#open_payment_';

        openPaymentClearCcSection();
        openPaymentPrepareOnlineCcShell();

        clearOpenPaymentSavedCards();
        openPaymentSetCardChoicePillDisabled(
            opModal().find(p + 'card_choice_new'),
            !uid,
            uid ? null : 'Select a member to continue'
        );
        if (!uid) {
            openPaymentSyncPaymentUiState();
            return;
        }

        hideOpenPaymentAjaxMsg();

        fetchOpenPaymentSavedPayments(uid).then(function (response) {
            const hasSaved = response.success && (response.saved_cards || []).length > 0;
            setOpenPaymentHasSavedCards(hasSaved);
            if (!hasSaved) {
                openPaymentSyncPaymentUiState();
                return;
            }
            setOpenPaymentSavedCards(response.saved_cards);
            openPaymentSyncPaymentUiState();
        });
    });

    $(document).off('click.openPayAddCardChoice', '#addOpenPaymentModal [data-cc-choice]');
    $(document).on('click.openPayAddCardChoice', '#addOpenPaymentModal [data-cc-choice]', function () {
        const p = '#open_payment_';
        const $pill = $(this);
        if ($pill.prop('disabled') || $pill.hasClass('is-disabled')) {
            return;
        }
        const choice = String($pill.data('cc-choice') || '');
        if (choice === 'new') {
            activateOpenPaymentNewCardUi();
            return;
        }

        if (!savedPaymentsState.hasSavedCards) {
            openPaymentSetCardChoicePillDisabled(
                opModal().find(p + 'card_choice_saved'),
                true,
                opIsMemberSession() ? 'No saved cards on your account' : 'This member has no saved cards'
            );
            return;
        }

        const uid = opModal().find('#open_payment_user_id').val();
        if (!uid) {
            showOpenPaymentAjaxMsg('alert-danger', '<li>Please select a member first.</li>');
            openPaymentSyncPaymentUiState();
            return;
        }

        fetchOpenPaymentSavedPayments(uid).then(function (response) {
            const hasSaved = response.success && (response.saved_cards || []).length > 0;
            setOpenPaymentHasSavedCards(hasSaved);
            if (!hasSaved) {
                return;
            }

            setOpenPaymentSavedCards(response.saved_cards);
            opModal().find(p + 'payment_type').val('saved');
            opModal().find('.open_pay_cc_manual').hide();
            opModal().find('.save_pay_div').show();
            openPaymentSyncCardChoicePillActive('saved');
            openPaymentEnableManualCc(false);
            openPaymentSetManualCcConstraints({ online: true, paymentType: 'saved' });
            openPaymentSyncPaymentUiState();
        });
    });
}

export function bindOpenPaymentAddModalDocumentHandlers() {
    $(document).off('click.openPayAddCancel', '[data-action="cancel-open-payment-modal"]');
    $(document).on('click.openPayAddCancel', '[data-action="cancel-open-payment-modal"]', function () {
        closeOpenPaymentModal({ action: 'cancel' });
    });

    $(document).off('select2:open.openPayAdd', '#open_payment_user_id');
    $(document).on('select2:open.openPayAdd', '#open_payment_user_id', function () {
        if (opCfg().kioskValidated || opHasPrefilledMember() || opIsMemberSession()) {
            return;
        }
        const select = this;
        setTimeout(function () {
            focusOpenSelect2Search(select);
        }, 0);
    });

    $(document).off('input.openPayAddCard', '#open_payment_card');
    $(document).on('input.openPayAddCard', '#open_payment_card', function () {
        formatCardNumberInput(this);
    });
}
